import React, { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import * as XLSX from "xlsx";
import SelectComp from "./core/SelectComp";
import { getUserInfo } from '../../utils/token';
import  { getCustomerDevicesList } from '../../api/endpoints/customer';
import { SendBulkMSG } from '../../api/endpoints/message';
import showSuccessAlert from '../../utils/swalAlerts/showSuccessAlert';
import showErrorAlert from "../../utils/swalAlerts/showErrorAlert";
import SampleBulkExcelDownload from "../common/SampleBulkExcelDownload";

const ALLOWED_FILE_TYPES = [
  "text/csv",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
];

const allowedFileTypes = {
  IMAGE: ["image/jpeg", "image/jpg", "image/png"],
  AUDIO: ["audio/mp3", "audio/mpeg", "audio/wav"],
  VIDEO: ["video/mp4", "video/mov", "video/avi"],
  DOCUMENT: [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ],
};


const BulkMessageForm = () => {
  const [companyId, setCompanyId] = useState(null);
  const [customerId, setCustomerId] = useState([]);
  const [projectTitle, setProjectTitle] = useState("");
  const [msgType, setMsgType] = useState("TEXT");
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [fileError, setFileError] = useState("");
  const [ deviceList, setDeviceList] = useState([]);
  const [ deviceId, setDeviceId] = useState();
  const [excelData, setExcelData] = useState([]);
  const [ error, setError ] = useState({});
   const [formData, setFormData] = useState({
    device: '',
    title: '',
    message: '',
    caption: '',
    file: null,
    excel: null,
  });

  const msgOptions = [
    { value: 'TEXT', label: 'Text' },
    { value: 'IMAGE', label: 'Image' },
    { value: 'AUDIO', label: 'Audio' },
    { value: 'VIDEO', label: 'Video' },
    { value: 'DOCUMENT', label: 'Document' },
  ];

  useEffect(()=>{
      async function fetchData() {
        const {compId, userId} = getUserInfo();
        setCompanyId(compId);
        setCustomerId(userId);
        const response  = await getCustomerDevicesList(compId, userId);
        //console.log(response);
        if(response.status === 200){
          console.log(response.data.data);
          const transformed = response.data.data.map((item) => ({
            value: item.device_id,
            label: item.mobile,
          }));
          setDeviceList(transformed);
          //console.log(transformed[0]['value']);
          if(!transformed?.[0]?.value){
            showErrorAlert({
              title: "Oops...! No active device found!",
              message: "No active device found, Please configure a device",
              icon: "error",
            })
          }else{
            setDeviceId(transformed[0].value)
          }
          
        }else{
          // show alert something went wrong
          showErrorAlert({
            title: "Oops...! Something went Wrong !",
            message: "Please try after sometime.",
            icon: "error",
          })
        }
      }
      fetchData();  
    },[]);



  // Simplified file handling — production dropzone setup will be more robust
  const onDrop = useCallback((acceptedFiles, fileRejections) => {
    setFileError("");
    setError({});
    if (fileRejections.length > 0) {
      setFileError("Only CSV or Excel files are allowed.");
      return;
    }

    const file = acceptedFiles[0]; // Assume one file
    setAttachedFiles([file]); // reset attached files

    const reader = new FileReader();
    reader.onload = (event) => {
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: "array" });

      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];

      //const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
      const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          defval: "",   // empty cell = ""
          raw: false,   // ensures human-readable values
        }).map((row) => {
          const newRow = {};
          Object.keys(row).forEach((key) => {
            newRow[key] = row[key] !== undefined && row[key] !== null
              ? String(row[key]) // force every value as string
              : "";
          });
          return newRow;
        });
      setExcelData(jsonData);
      console.log("Parsed Excel Data:", jsonData);
    };
    reader.readAsArrayBuffer(file);
  }, []);


  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: ALLOWED_FILE_TYPES,
    onDrop,
    multiple: true,
    maxFiles: 1 // Optional: set as needed
  });

  const removeFile = (idx) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== idx));
    setExcelData([]);
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      setFormData({ ...formData, [name]: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const onStart = async() => {

    if(!excelData){
      setError({excelDataError : 'Excel file is not uploaded. Please upload excel file.'});
      return false;
    }
   
    const fileUpload = new FormData();
    
    fileUpload.append('device_id', deviceId);
    fileUpload.append('msg_type', msgType);
    fileUpload.append('title', projectTitle);
    fileUpload.append('comp_id', companyId);
    fileUpload.append('customer_id', customerId);
    fileUpload.append('rows', JSON.stringify(excelData));

    switch (msgType) {
      case 'TEXT':
        fileUpload.append('text', formData.message);
        break;
      case 'IMAGE':
      case 'AUDIO':
      case 'VIDEO':
      case 'DOCUMENT':
        fileUpload.append('file', formData.file);
        fileUpload.append('caption', formData.caption);
        fileUpload.append('fileName', formData.file?.name);
        fileUpload.append('mimetype', formData.file?.type);        
        break;
      default:
        break;
    }
    
    let response = await SendBulkMSG(fileUpload);
    if(response.status === 200){
      if(response.data.success === true){
        showSuccessAlert({
          title: "Successful!",
          message: response.data.message,
          icon: "success",
          cancelText : 'Ok',
          onBack: () => window.location.href = "/customer/batch/list",
        })
      }else{
        showErrorAlert({
          title: "Warning!!!!",
          message: response.data.message,
          icon: "error",
        })
      }
    }else{
      showErrorAlert({
        title: "Oops...! Something went Wrong !",
        message: "Please try after sometime.",
        icon: "error",
      })
    }
  }

  return (
    <div className="col-lg-12">
      <div className="card">
        <div className="card-body">
          <div className="col-lg-4">
            <div className="mb-3">
              <label htmlFor="choices-priority-input" className="form-label">
                Device 
              </label>
              < SelectComp 
                val={deviceId}
                setVal={setDeviceId}
                options={deviceList}
                selectName ="msgType"
              />
            </div>
          </div>
          <div className="col-lg-4">
            <div className="mb-3">
              <label htmlFor="choices-priority-input" className="form-label">
                Message Type
              </label>
              < SelectComp 
                val={msgType}
                setVal={setMsgType}
                options={msgOptions}
                selectName ="msgType"
              />
            </div>
          </div>

          <div className="mb-3 ">
            <label className="form-label" htmlFor="project-title-input">
              Title
            </label>
            <input
              type="text"
              className="form-control"
              id="project-title-input"
              placeholder="Enter title"
              value={projectTitle}
              onChange={(e) => setProjectTitle(e.target.value)}
            />
          </div>
          {(msgType === 'IMAGE' || msgType === 'AUDIO' || msgType === 'VIDEO' || msgType === 'DOCUMENT') && (
            <>
              <div className="mb-3">
                <label className="form-label" htmlFor="project-thumbnail-img">
                  Uplaod Image
                </label>
                <input
                  className="form-control"
                  id="project-thumbnail-img"
                  type="file"
                  name="file"
                  accept={allowedFileTypes[msgType]?.join(",")}
                  onChange={handleChange}
                />
              </div>
              <div className="mb-3 ">
                <label className="form-label" htmlFor="project-title-input">
                  Caption
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="project-title-input"
                  placeholder="Enter Caption"
                  name="caption"
                  onChange={handleChange}
                />
              </div>
            </>
          )}
          {msgType === 'TEXT' && (
          <div className="mb-3">
            <label className="form-label">Message</label>
            <div id="ckeditor-classic">
              {/* Replace with a proper WYSIWYG editor for production use */}
              <textarea
                className="form-control"
                rows={6}
                placeholder="Type your message here"
                name="message"
                onChange={handleChange}
              />
            </div>
          </div>
          )}

          <SampleBulkExcelDownload />
        </div>
      </div>

      {/* Attached files Card */}
      <div className="card">
        <div className="card-header">
          <h5 className="card-title mb-0">Attached Excel With Mobile Number</h5>
        </div>
        <div className="card-body">
          <div>
            <p className="text-muted">Add excel files here.</p>
            
            {/* Dropzone */}
            <div {...getRootProps()} className="dropzone" style={{ border: "2px dashed #ced4da", padding: "24px", borderRadius: "8px", textAlign: "center", background: isDragActive ? "#f8f9fa" : "white", cursor: "pointer" }}>
              <input {...getInputProps()} />
              <div className="dz-message needsclick">
                <div className="mb-3">
                  <i className="display-4 text-muted ri-upload-cloud-2-fill"></i>
                </div>
                {isDragActive ? (
                  <h5>Drop files here...</h5>
                ) : (
                  <h5>Drop files here or click to upload.</h5>
                )}
                <div className="text-muted fs-13 mt-2">
                  Only CSV and Excel files allowed.
                </div>
                {fileError && (
                  <div className="text-danger mt-2">{fileError}</div>
                )}
                {error.excelDataError && (
                  <div className="text-danger mt-2">{error.excelDataError}</div>
                )}
              </div>
            </div>

            {/* Preview List */}
            <ul className="list-unstyled mb-0" id="dropzone-preview">
              {attachedFiles.map((file, idx) => (
                <li className="mt-2" key={idx}>
                  <div className="border rounded">
                    <div className="d-flex p-2">
                      <div className="flex-shrink-0 me-3">
                        <div className="avatar-sm bg-light rounded d-flex align-items-center justify-content-center" style={{height: 38, width: 38}}>
                          <i className="ri-file-excel-2-line fs-24 text-success"></i>
                        </div>
                      </div>
                      <div className="flex-grow-1">
                        <div className="pt-1">
                          <h5 className="fs-14 mb-1">{file.name}</h5>
                          <p className="fs-13 text-muted mb-0">
                            {(file.size / 1024).toFixed(2)} KB
                          </p>
                        </div>
                      </div>
                      <div className="flex-shrink-0 ms-3">
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => removeFile(idx)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    <div className="d-flex p-2">
                      {excelData.length > 0 && (
                        <div className="mt-4 flex-grow-1">
                          <h6>Preview Excel Data:</h6>
                          <pre style={{ maxHeight: 200, overflowY: "auto" }}>
                            {JSON.stringify(excelData, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>

                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="text-end mb-4">
        <button type="button" className="btn btn-success w-sm" onClick={() => {onStart()}}>
          Start
        </button>
      </div>
    </div>
  );
};

export default BulkMessageForm;
